import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import {
  Autocomplete,
  Box,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MultiSectionDigitalClock } from '@mui/x-date-pickers/MultiSectionDigitalClock';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  formatDateFilterDisplay,
  RELATIVE_OPTIONS,
  RelativeDateOption,
  resolveTypedValue,
} from './unifiedDateFilterValue';
import { dateFilterValueFormat, normalizeDateFilterValue } from '../../../utils/dateFilterFormat';

export interface UnifiedDateFilterFieldProps {
  /** Field-level label shown above the control (e.g. "Date", "From", "To"). */
  label: string;
  /** Current value: a relative code or an absolute date (`YYYY-MM-DD`, or
   *  `YYYY-MM-DD HH:mm` for a with-time field). */
  value: string;
  /** Emit the new value (relative code or wire-format date); empty string clears. */
  onChange: (value: string) => void;
  /** Accessible label for the text input. */
  ariaLabel: string;
  /** When set, the field captures and stores a time component alongside the
   *  date, and the calendar popover gains a clock. */
  withTime?: boolean;
}

/**
 * A single date-filter control that unifies exact and relative selection.
 *
 * Users can type to filter the relative options (e.g. "today", "first day of
 * last month") and pick one, or open the calendar to choose an exact date (and
 * time, for with-time fields). There is no separate mode toggle: the control
 * resolves which the user meant from how they entered it. The emitted value
 * keeps the existing wire shape so downstream filter conversion is unchanged.
 */
export const UnifiedDateFilterField: React.FC<UnifiedDateFilterFieldProps> = ({
  label,
  value,
  onChange,
  ariaLabel,
  withTime,
}) => {
  const theme = useTheme();
  const valueFormat = dateFilterValueFormat(withTime);
  const [calendarAnchor, setCalendarAnchor] = useState<HTMLElement | null>(null);
  // The Autocomplete input text is controlled so unresolved free-typed text can be
  // reset on blur, keeping the visible text in step with the committed value.
  const [inputText, setInputText] = useState(() => formatDateFilterDisplay(value, withTime));
  // While the field is focused we let the user type freely without reformatting
  // under their cursor; the value-sync below only re-normalises the text on blur.
  const focusedRef = useRef(false);

  useEffect(() => {
    if (focusedRef.current) return;
    setInputText(formatDateFilterDisplay(value, withTime));
  }, [value, withTime]);

  // Derive the Autocomplete's selected option from the stored value. A relative
  // code maps to its catalogue entry; an absolute date becomes a synthetic
  // option (tolerated because the Autocomplete is freeSolo).
  const selectedOption = useMemo<RelativeDateOption | null>(() => {
    if (!value) return null;
    const relative = RELATIVE_OPTIONS.find((option) => option.value === value);
    if (relative) return relative;
    return { label: formatDateFilterDisplay(value, withTime), value };
  }, [value, withTime]);

  // The date/time the pickers operate on. A stored absolute value parses to a
  // real date; anything else (empty or a relative code) falls back to today at
  // midnight so a freshly opened calendar has a sensible reference.
  const calendarValue = useMemo(() => {
    if (!value) return null;
    const parsed = dayjs(normalizeDateFilterValue(value), valueFormat, true);
    return parsed.isValid() ? parsed : null;
  }, [value, valueFormat]);

  const pickerValue = calendarValue ?? dayjs().startOf('day');

  const commitPicked = (next: Dayjs | null) => {
    if (next && next.isValid()) onChange(next.format(valueFormat));
  };

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
        {label}
      </Typography>
      <Autocomplete
        freeSolo
        size="small"
        options={RELATIVE_OPTIONS}
        value={selectedOption}
        inputValue={inputText}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
        isOptionEqualToValue={(option, selected) => option.value === selected.value}
        // Selecting a relative option commits its code; clearing empties the
        // filter; free-typed text (a date like "12/6/2026" or a relative
        // phrase) is resolved on Enter. Unmatched text never reaches the wire.
        onChange={(_event, next) => {
          if (next && typeof next === 'object') {
            onChange(next.value);
          } else if (next === null) {
            onChange('');
          } else if (typeof next === 'string') {
            // Backspacing the whole value and pressing Enter should clear the
            // filter (consistent with the Clear icon), not no-op.
            if (!next.trim()) {
              onChange('');
              return;
            }
            const resolved = resolveTypedValue(next, withTime);
            if (resolved) onChange(resolved);
          }
        }}
        // Resolve a typed date/phrase as soon as it is complete, so it still
        // applies when the user clicks Apply without pressing Enter first.
        // `reason === 'input'` excludes programmatic resets when `value` syncs.
        onInputChange={(_event, input, reason) => {
          // Keep the controlled input in step with typing, selections and clears.
          setInputText(input);
          if (reason !== 'input') return;
          const resolved = resolveTypedValue(input, withTime);
          if (resolved) onChange(resolved);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={
              withTime
                ? 'Type e.g. today or 12/06/2026 09:00, or pick a date'
                : 'Type e.g. today or 12/06/2026, or pick a date'
            }
            onFocus={() => {
              focusedRef.current = true;
            }}
            onBlur={() => {
              // Re-normalise to the committed value's display: this both discards
              // unresolved/partial free text and canonicalises a resolved date
              // (e.g. "12/6/2026" -> "12/06/2026"), so the field never drifts.
              focusedRef.current = false;
              setInputText(formatDateFilterDisplay(value, withTime));
            }}
            inputProps={{ ...params.inputProps, 'aria-label': ariaLabel }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  <InputAdornment position="end" sx={{ mr: 0 }}>
                    <IconButton
                      aria-label={`${ariaLabel} open calendar`}
                      size="small"
                      edge="end"
                      onClick={(event) => setCalendarAnchor(event.currentTarget)}
                    >
                      <CalendarMonthIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': { fontSize: '0.875rem' },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.v4.filterInputBorder,
              },
            }}
          />
        )}
      />
      <Popover
        open={Boolean(calendarAnchor)}
        anchorEl={calendarAnchor}
        onClose={() => setCalendarAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {/* MUI's Popover is Modal-based and moves focus into the popover on
            open, so the calendar is keyboard-reachable without `autoFocus`
            (which jsx-a11y/no-autofocus forbids). */}
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          {withTime ? (
            // Date + time: the calendar sets the day (preserving the current
            // time) and the clock sets the time (preserving the current day),
            // both committing on change. The popover stays open so the user can
            // set both before dismissing it.
            <Box sx={{ display: 'flex' }}>
              <DateCalendar value={pickerValue} onChange={commitPicked} />
              <MultiSectionDigitalClock
                value={pickerValue}
                onChange={commitPicked}
                aria-label={`${ariaLabel} time`}
                // Force 24-hour entry to match the HH:mm display and the
                // 24-hour-only typed formats, regardless of the picker locale.
                ampm={false}
                sx={{ borderLeft: `1px solid ${theme.palette.v4.border}` }}
              />
            </Box>
          ) : (
            <DateCalendar
              value={calendarValue}
              onChange={(next) => {
                commitPicked(next);
                setCalendarAnchor(null);
              }}
            />
          )}
        </LocalizationProvider>
      </Popover>
    </Box>
  );
};

export { formatDateFilterDisplay } from './unifiedDateFilterValue';
